import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MainContainer from "../../../layout/MainContainer";
import { Button, MenuItem, Select } from "@mui/material";
import { useFormik } from "formik";
import { showError, showToast } from "../../../constants/toasts";
import {
    useAddVoiceMutation,
    useLazyGetVoiceByIdQuery,
    useUpdateVoiceMutation,
} from "../../../services/aiVoice";
import "react-phone-input-2/lib/bootstrap.css";
import { useLazyGetSettingsQuery } from "../../../services/settings";
import { VOICE_TYPE, VOICE_TYPE_NAME } from "../../../utils/enums";
import AudioUpload from "../../../components/AudioUpload";
import { API_URL } from "../../../constants/url";

type VoiceFormValues = {
    voice_type: string;
    age_group: string;
    audio: string;
};

const AddVoice = () => {

    const navigate = useNavigate();
    const { id } = useParams();
    const [addVoice] = useAddVoiceMutation();
    const [updateVoice] = useUpdateVoiceMutation();
    const [getVoiceById] = useLazyGetVoiceByIdQuery();
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [data, setData] = useState<any[]>([]);
    const [currentAudioFile, setCurrentAudioFile] = useState<string>("");
    const isEditMode = useMemo(() => Boolean(id), [id]);
    const [getSettings] = useLazyGetSettingsQuery()

    const handleAudioUploadSuccess = (audioData: any) => {
        formik.setFieldValue("audio", audioData.id);
        setCurrentAudioFile(""); // Clear current audio file when new one is uploaded
    };

    const getAgeList = async () => {
        setIsLoading(true);
        try {
            const response = await getSettings({
                length: 100, 
                start: 1,
                search: "",
            }).unwrap();
            if (response.status === 200) {
                setData(response?.data?.response_object || []);
            }
            setIsLoading(false);
        } catch (error) {
            console.log(error);
            setIsLoading(false);
        }
    };
    const formik = useFormik<VoiceFormValues>({
        initialValues: {
            voice_type: "",
            age_group: "",
            audio: "",
        },
        enableReinitialize: true,
        onSubmit: async (values, { setSubmitting }) => {
            const trimmed = {
                voice_type: values.voice_type?.toString().trim(),
                age_group: values.age_group?.toString().trim(),
                audio: values.audio?.toString().trim(),
            };

            if (!trimmed.voice_type || !trimmed.age_group || !trimmed.audio) {
                showError("All fields are required");
                setSubmitting(false);
                return;
            }

            try {
                const voiceData = {
                    voice_type: trimmed.voice_type,
                    age_group: trimmed.age_group,
                    audio: trimmed.audio,
                };

                if (isEditMode) {
                    const res: any = await updateVoice({ body: voiceData, id: parseInt(id as string, 10) }).unwrap();
                    if (res?.status === 200 || res?.status === 201) {
                        showToast("Voice updated successfully");
                        navigate(-1);
                    }
                } else {
                    const res: any = await addVoice({ body: voiceData }).unwrap();
                    if (res?.status === 200 || res?.status === 201) {
                        showToast("Voice added successfully");
                        navigate(-1);
                    }
                }
            } catch (e: any) {
                showError(e?.data?.message || "Something went wrong");
            } finally {
                setSubmitting(false);
            }
        },
    });

    // Load age groups on component mount
    useEffect(() => {
        getAgeList();
    }, []);

 
    useEffect(() => {
        const fetchDetail = async () => {
            if (!id) return;
            try {
                const response: any = await getVoiceById({ id: Number(id) }).unwrap();
                if (response?.status === 200) {
                    const item = Array.isArray(response.data) ? response.data[0] : response.data;
                    if (item) {
                        formik.setValues({
                            voice_type: item?.voice_type?.toString() ?? "",
                            age_group: item?.age_group?.id?.toString() ?? "",
                            audio: item?.audio?.toString() ?? "",
                        });
                        setCurrentAudioFile(item?.audio?.toString() ?? "");
                    }
                }
            } catch (e: any) {
                showError(e?.data?.message || "Failed to load voice details");
            }
        };
        fetchDetail();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    return (
        <>
            <div className="main_title">
                <h1>{isEditMode ? "Edit Voice" : "Add Voice"}</h1>
                <p>
                    <span onClick={() => navigate("/dashboard")}>Dashboard</span>
                    {" "} - <span onClick={() => navigate("/voice-library")}>Voice Library</span>
                    {" "} - {isEditMode ? "Edit Voice" : "Add Voice"}
                </p>
            </div>

            <div className="cards">
                <div className="form">
                    <div className="gap_p">
                        <div className="control_group w_33">
                            <label>Voice Type</label>
                            <Select
                                fullWidth
                                displayEmpty
                                value={formik.values.voice_type || ""}
                                onChange={(e) => formik.setFieldValue("voice_type", e.target.value)}
                                onBlur={() => formik.setFieldTouched("voice_type", true)}
                            >
                                <MenuItem disabled value="">
                                    Select
                                </MenuItem>
                                {Object.entries(VOICE_TYPE_NAME).map(([key, label]) => (
                                    <MenuItem key={key} value={key}>
                                        {label}
                                    </MenuItem>
                                ))}
                            </Select>
                            {formik.touched.voice_type && !formik.values.voice_type ? (
                                <h6 className="err_msg">Voice Type is required</h6>
                            ) : ""}
                        </div>
                        <div className="control_group w_33">
                            <label>Age Group</label>
                            <Select
                                fullWidth
                                displayEmpty
                                value={formik.values.age_group || ""}
                                onChange={(e) => formik.setFieldValue("age_group", e.target.value)}
                                onBlur={() => formik.setFieldTouched("age_group", true)}
                                disabled={isLoading}
                            >
                                <MenuItem disabled value="">
                                    {isLoading ? "Loading..." : "Select"}
                                </MenuItem>
                                {data.map((item) => (
                                    <MenuItem key={item.id} value={item.id}>
                                        {item.start_range}-{item.end_range}
                                    </MenuItem>
                                ))}
                            </Select>
                            {formik.touched.age_group && !formik.values.age_group ? (
                                <h6 className="err_msg">Age Group is required</h6>
                            ) : ""}
                        </div>
                    </div>
                    <div className="gap_p">
                        <div className="control_group w_50">
                            <label>Voice File</label>
                            <AudioUpload
                                onUploadSuccess={handleAudioUploadSuccess}
                                disabled={formik.isSubmitting}
                                className="voice-upload"
                            />
                            {formik.touched.audio && !formik.values.audio ? (
                                <h6 className="err_msg">Audio file is required</h6>
                            ) : ""}
                            {isEditMode && currentAudioFile && !formik.values.audio && (
                                <div style={{ marginTop: "8px" }}>
                                    <p style={{ color: "#666", fontSize: "12px", marginBottom: "8px" }}>
                                        Current audio file:
                                    </p>
                                    <audio controls style={{ width: '100%', maxWidth: '300px' }}>
                                        <source src={`${API_URL}/media/${currentAudioFile}`} type="audio/mpeg" />
                                        Your browser does not support the audio element.
                                    </audio>
                                </div>
                            )}
                            {formik.values.audio && (
                                <p style={{ color: "#1976d2", fontSize: "12px", marginTop: "8px" }}>
                                    ✓ Audio file uploaded successfully
                                </p>
                            )}
                        </div>
                    </div>
                    <div className="form_btn">
                        <Button
                            className="btn btn_primary"
                            onClick={() => {
                                formik.setTouched({ voice_type: true, age_group: true, audio: true });
                                formik.handleSubmit();
                            }}
                            disabled={formik.isSubmitting}
                        >
                            {formik.isSubmitting ? "Processing..." : isEditMode ? "Update" : "Save"}
                        </Button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AddVoice;
